// Single source for the AI Operations Suite pages (/ai-operations, its
// step pages, the home-page band, and the /demo tour). Every readiness label
// here traces to server/docs/23controls — see
// docs/arcs/AI_OPERATIONS_SUITE_ARC.md §0 before changing a claim.

export const SIGNUP_BASE = 'https://23controls.com'

// All subscription CTAs go through here so they carry consistent UTM tags
// into 23Controls and can be repointed in one place.
export function funnelUrl(path = '/signup', content = 'ai-operations') {
  const params = new URLSearchParams({
    utm_source: 'synthetiqsignals',
    utm_medium: 'ai-operations-suite',
    utm_content: content,
  })
  return `${SIGNUP_BASE}${path}?${params.toString()}`
}

export const readiness = {
  available: { label: 'Available', className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  pilot: { label: 'Pilot', className: 'bg-ember-500/15 text-ember-400 border-ember-500/30' },
  building: { label: 'Building', className: 'bg-signal-500/15 text-signal-300 border-signal-500/30' },
  early: { label: 'Early access', className: 'bg-synth-500/15 text-synth-300 border-synth-500/30' },
}

export const suite = {
  name: 'AI Operations Suite',
  eyebrow: 'Conductor · AI Operations Suite',
  headline: 'Run AI like an operation, not an experiment.',
  summary:
    'The AI Operations Suite is the working surface of Graffiti™ OS: shape a request, read and question your documents, turn the steps into a workflow, run it on a schedule, and measure what worked — locally, on hardware you control, under 23Controls™ governance. Conductor is the orchestration engine underneath, built on Apache Airflow.',
  pillars: [
    { title: 'Local first', detail: 'Retrieval (RAG) and workflows (DAGs) run inside your Graffiti™ enclave. Your documents are not shipped to someone else\'s cloud to be useful.' },
    { title: 'Traceable', detail: 'Answers cite the passages they came from, and abstain when the evidence is not there — instead of guessing.' },
    { title: 'Governed', detail: 'Every action traces back to a verified Identity Anchor, runs past policy, and lands in an audit trail.' },
  ],
}

// Ordered: this is the step path that ends in the subscription funnel.
export const steps = [
  {
    id: 'prompt-shaping',
    name: 'Prompt Shaping',
    verb: 'Ask',
    status: 'pilot',
    tagline: 'Turn a loose question into a precise, routed request.',
    description:
      'Type — or speak — what you want in plain language. Prompt Shaping grounds it against your own Collections, proposes which specialist (Tribe) should handle it with a confidence score, and asks you a clarifying question in the same turn when the request is ambiguous, rather than guessing.',
    flow: [
      { label: 'Ground', detail: 'Matches your words against documents and entities you already have.' },
      { label: 'Route', detail: 'Ranks the Tribes that could answer, each with a confidence score.' },
      { label: 'Clarify', detail: 'Below the confidence floor, it asks you instead of guessing.' },
      { label: 'Gate', detail: 'Sensitive routes are checked against policy before handoff.' },
    ],
    views: [
      'Console view — a dark operations layout with live grounding hits as pickable chips',
      'Wizard view — the same flow as a guided, step-by-step stepper',
    ],
    honesty:
      'Built and running in the 23Controls™ workspace. Live handoff today goes to the Academic Tribe; other Tribes are routed and scored but not yet handed off. Confidence thresholds are still being calibrated on real usage.',
  },
  {
    id: 'docflow',
    name: 'DocFlow',
    verb: 'Read',
    status: 'pilot',
    tagline: 'Bring a document in. Talk it through. Keep the trail.',
    description:
      'DocFlow is the document read-and-treat space: a file comes in, its text is extracted and indexed inside your enclave, and you work through it in conversation — asking what it says, where, and what needs attention. It sits on the same foundation as ForensIQ, so every document keeps its record of where it came from and what was done with it.',
    flow: [
      { label: 'Ingest', detail: 'Upload a document; text is extracted (OCR included) inside your enclave.' },
      { label: 'Index', detail: 'Content is split and indexed locally for retrieval (RAG).' },
      { label: 'Converse', detail: 'Ask about the document in plain language and follow up in dialog.' },
      { label: 'Record', detail: 'Each document keeps its provenance and analysis on the ForensIQ foundation.' },
    ],
    views: [
      'Document dialog — questions and answers grounded in the uploaded file',
      'Document record — what came in, when, and what was done with it',
    ],
    honesty:
      'The DocFlow chat path is working in the 23Controls™ workspace. Its move onto the shared ForensIQ document foundation is in progress, and connecting it to cite-and-verify retrieval — so every answer carries checked citations — is the next step.',
  },
  {
    id: 'conductor-canvas',
    name: 'Conductor Canvas',
    verb: 'Automate',
    status: 'pilot',
    tagline: 'Draw the workflow. Conductor turns it into a running DAG.',
    description:
      'Drag steps onto a canvas — ingest, enrich, human review, deliver — connect them, and configure each one in a side drawer. Activating the workflow generates a real Apache Airflow DAG and deploys it to the scheduler on your unit.',
    flow: [
      { label: 'Compose', detail: 'Drag nodes from the palette and connect them.' },
      { label: 'Configure', detail: 'Set each step\'s parameters in the config drawer.' },
      { label: 'Save', detail: 'The canvas is stored with your workflow.' },
      { label: 'Activate', detail: 'Conductor generates the DAG and hands it to Airflow.' },
    ],
    views: [
      'Command Deck canvas — dark ops-console chrome, focus mode, collapsible rails',
      'Config drawer — per-node settings beside the canvas',
    ],
    honesty:
      'Single-step workflows are verified end to end against a live Airflow scheduler. Multi-step workflows are being built.',
  },
  {
    id: 'workflow-catalog',
    name: 'Workflow Catalog & Scheduler',
    verb: 'Operate',
    status: 'pilot',
    tagline: 'Every workflow in one place, every run on the board.',
    description:
      'The Workflow Catalog lists your workflows with status and filters. The Scheduler shows a live run queue — what is running, what just finished, what is next — alongside your scheduled workflows and a CRON reference for setting cadence.',
    flow: [
      { label: 'Catalog', detail: 'Browse, filter, and open your workflows.' },
      { label: 'Schedule', detail: 'Set a cadence with standard CRON syntax.' },
      { label: 'Watch', detail: 'A live run queue refreshes every few seconds.' },
      { label: 'Reuse', detail: 'A library of starter workflow templates (coming next).' },
    ],
    views: [
      'Workflow list — cards, status chips, filters',
      'Scheduler — live run queue plus scheduled workflows',
    ],
    honesty:
      'The workflow list and Scheduler are built. The starter-template library is planned, not shipped.',
  },
  {
    id: 'attribution',
    name: 'Attribution',
    verb: 'Measure',
    status: 'early',
    tagline: 'Every touchpoint weighted. Every weight explainable.',
    description:
      'Attribution decides how much credit each touchpoint in a customer journey deserves. Instead of an even split or a hand-tuned decay curve, it weights touchpoints with a Fibonacci sequence sized to that journey\'s own length — so it scales from two touchpoints to fifty, and gives the same, auditable answer every time. And it runs locally, so your journey data does not have to leave home to be analyzed.',
    flow: [
      { label: 'Capture', detail: 'Journeys and their touchpoints are recorded across channels.' },
      { label: 'Weight', detail: 'Fibonacci Decay weights each touchpoint, sized to the journey.' },
      { label: 'Explain', detail: 'Deterministic: the same journey always gets the same, documented weights.' },
      { label: 'Orchestrate', detail: 'Always-on runs through Conductor workflows (coming next).' },
    ],
    views: [
      'Attribution runs and win/loss analysis by channel (early access)',
      'Coming next: a workspace view and Curator-surfaced insights',
    ],
    honesty:
      'The attribution engine is built and tested, with journey tracking, attribution runs, and channel win/loss analysis. Running it on a schedule through Conductor, the insight layer, and a customer-facing view are roadmap. It is not yet a subscription line item — today it is early access.',
    secondaryCta: { label: 'Request early access', href: '/support/contact/sales' },
  },
  {
    id: 'governed',
    name: 'Governed by 23Controls™',
    verb: 'Govern',
    status: 'available',
    tagline: 'Accountable by default — and that is where your subscription starts.',
    description:
      'Everything above runs under 23Controls™: a verified Identity Anchor behind every unit and action, a compliance rulebook applied at ingest, a human-in-the-loop review queue for anything ambiguous, and one audit trail. Your 23Controls™ subscription is how you get the suite.',
    flow: [
      { label: 'Identify', detail: 'Create an account and your Identity Anchor.' },
      { label: 'Subscribe', detail: 'Choose a plan on 23Controls™.' },
      { label: 'Deploy', detail: 'Your Graffiti™ image is generated for your unit.' },
      { label: 'Operate', detail: 'Boot it, sign in, and open the AI Operations Suite.' },
    ],
    views: [
      'Compliance rulebook and audit trail',
      'Human review queue for escalations',
    ],
    honesty:
      'Account creation, identity binding, the compliance rulebook, and the audit trail are live. Device first boot and entity review are in pilot.',
  },
]

export function stepIndex(id) {
  return steps.findIndex((s) => s.id === id)
}
